import ColorPicker, { ColorPresets } from './ColorPicker';

const AppointmentDesign = ({ config, style, onConfigChange, onStyleChange }) => {
    const widgetWidth = config.widgetWidth || '1240';

    // Style defaults
    const backgroundColor = style.backgroundColor || '#FFFFFF';
    const textColor = style.textColor || '#000000';
    const accentColor = style.accentColor || '#4A90E2';
    const fontFamily = style.fontFamily || 'default';

    const fontOptions = [
        { value: 'default', name: 'Default (System)' },
        { value: 'inter', name: 'Inter' },
        { value: 'roboto', name: 'Roboto' },
        { value: 'open-sans', name: 'Open Sans' },
        { value: 'lato', name: 'Lato' },
        { value: 'montserrat', name: 'Montserrat' },
        { value: 'poppins', name: 'Poppins' },
        { value: 'source-sans-pro', name: 'Source Sans Pro' },
        { value: 'nunito', name: 'Nunito' },
        { value: 'raleway', name: 'Raleway' },
        { value: 'ubuntu', name: 'Ubuntu' }
    ];

    const widthOptions = [
        { value: '320', label: '320px (Mobile)' },
        { value: '480', label: '480px (Small)' },
        { value: '640', label: '640px (Medium)' },
        { value: '800', label: '800px (Large)' },
        { value: '1024', label: '1024px (XL)' },
        { value: '1240', label: '1240px (2XL)' },
        { value: '100%', label: '100% (Full Width)' }
    ];

    const handleWidthChange = (width) => {
        onConfigChange({ widgetWidth: width });
    };

    return (
        <div className="p-4 h-full overflow-y-auto">
            <div className="max-w-2xl mx-auto">
                <h3 className="text-lg font-semibold mb-2">Design & Styling</h3>
                <p className="text-sm text-gray-400 mb-8">
                    Customize the appearance and styling of your appointment widget
                </p>

                <div className="space-y-8">

                    {/* Widget Width */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Widget Width</h4>

                        <select
                            value={widgetWidth}
                            onChange={(e) => handleWidthChange(e.target.value)}
                            className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                        >
                            {widthOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Choose the width of your appointment widget. Full width adapts to container size.
                        </p>
                    </div>

                    {/* Colors */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Colors</h4>

                        <div className="space-y-6">
                            {/* Background Color */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Background Color
                                </label>
                                <ColorPicker
                                    value={backgroundColor}
                                    onChange={(color) => onStyleChange({ backgroundColor: color })}
                                    label="Background Color"
                                    showHex={true}
                                    size="default"
                                />
                                <div className="mt-3">
                                    <ColorPresets
                                        selectedColor={backgroundColor}
                                        onColorSelect={(color) => onStyleChange({ backgroundColor: color })}
                                    />
                                </div>
                            </div>

                            {/* Text Color */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Text Color
                                </label>
                                <ColorPicker
                                    value={textColor}
                                    onChange={(color) => onStyleChange({ textColor: color })}
                                    label="Text Color"
                                    showHex={true}
                                    size="default"
                                />
                                <div className="mt-3">
                                    <ColorPresets
                                        selectedColor={textColor}
                                        onColorSelect={(color) => onStyleChange({ textColor: color })}
                                    />
                                </div>
                            </div>

                            {/* Accent Color */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Accent Color
                                </label>
                                <p className="text-xs text-gray-500 mb-3">
                                    Used for buttons, selected states, and interactive elements
                                </p>
                                <ColorPicker
                                    value={accentColor}
                                    onChange={(color) => onStyleChange({ accentColor: color })}
                                    label="Accent Color"
                                    showHex={true}
                                    size="large"
                                />
                                <div className="mt-3">
                                    <ColorPresets
                                        selectedColor={accentColor}
                                        onColorSelect={(color) => onStyleChange({ accentColor: color })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Typography */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-4 border-b border-gray-700 pb-2">Typography</h4>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Font Family
                            </label>
                            <select
                                value={fontFamily}
                                onChange={(e) => onStyleChange({ fontFamily: e.target.value })}
                                className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                            >
                                {fontOptions.map((font) => (
                                    <option key={font.value} value={font.value}>
                                        {font.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                Choose a font that matches your website&apos;s typography
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentDesign;
